import type { ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface Props {
  /** The element that opens the dialog when clicked (typically a button). */
  trigger: ReactNode;
  /** Dialog title (e.g. "Delete skill?"). */
  title: string;
  /** Body text — supports rich children (e.g. wrapping a name in <strong>). */
  description: ReactNode;
  /** Confirm-button label (e.g. "Delete", "Abandon", "Reject"). */
  confirmLabel: string;
  /**
   * Invoked when the user confirms.
   *
   * Error-handling contract (intentional, fire-and-forget):
   * - The dialog closes as soon as the user clicks confirm (AlertDialogAction's
   *   default behavior). Async work continues in the background.
   * - Errors are NOT surfaced by this component. Callers must wire error
   *   handling at the page/component level (e.g. via React Query mutation
   *   error state, or a parent-level `runAction`/`setError` helper).
   * - If the caller passes an `async` function, we attach a `.catch()` so an
   *   unhandled rejection does not crash the render — but the rejection is
   *   *swallowed* here. That is by design; surface the error upstream.
   *
   * If you need pending state or in-dialog error display, build a different
   * component — do not retrofit onto this one.
   */
  onConfirm: () => void | Promise<void>;
}

/**
 * Generic confirmation dialog for destructive actions.
 *
 * Wraps shadcn's AlertDialog with the trigger included as a slot, so callers
 * pass their existing action button as the `trigger` prop. The Action button
 * is destructive-styled by default (per `AlertDialogAction` in
 * `@/components/ui/alert-dialog`).
 *
 * Use this instead of `window.confirm()` so destructive flows match the
 * design system across the app.
 */
export function ConfirmDialog({
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
}: Props): React.ReactElement {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>{description}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(): void => {
              // Caller's onConfirm is fire-and-forget; errors are the caller's
              // responsibility (see Props docstring). We attach .catch() to
              // prevent an unhandled-rejection crash if the caller passed an
              // async function whose promise we don't await.
              const result = onConfirm();
              if (result instanceof Promise) {
                result.catch((): void => {
                  // Intentionally swallowed — see Props docstring.
                });
              }
            }}
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
